import { useEffect, useState } from 'react'
import { Modal } from 'stories/components'

import type { IAccountsReceivableSettings } from './types'

const inputClass = 'border border-gray-300 rounded px-2 py-1.5 w-full text-sm'

export const AccountsReceivableSettingsModal = ({
  settings,
  onClose,
  onSave,
}: {
  settings: IAccountsReceivableSettings
  onClose: () => void
  onSave: (data: Record<string, any>) => Promise<void>
}) => {
  const [form, setForm] = useState(settings)
  const [daysText, setDaysText] = useState((settings.daysBefore || []).join(', '))
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setForm(settings)
    setDaysText((settings.daysBefore || []).join(', '))
  }, [settings])

  const set = (patch: Partial<IAccountsReceivableSettings>) => setForm((prev) => ({ ...prev, ...patch }))

  const onSubmit = async () => {
    setSaving(true)
    try {
      await onSave({
        ...form,
        startingInvoiceNo: Math.max(1, Math.floor(Number(form.startingInvoiceNo) || 1)),
        daysBefore: daysText
          .split(/[, ]+/)
          .map((part) => Number(part))
          .filter((part) => Number.isFinite(part) && part >= 0),
        overdueEveryDays: Number(form.overdueEveryDays) || 0,
      })
      return true
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title="Receivable reminders" titleOkay="Save" loading={saving} isOpen onClose={onClose} onOk={onSubmit} childLevel={1}>
      <div className="w-[560px] max-w-full grid grid-cols-1 gap-3 text-sm">
        <p className="text-gray-600">
          Unpaid invoices on the global schedule are emailed each morning. An invoice can use this schedule, its own,
          or none. A paid notice goes out when an invoice is marked paid.
        </p>
        <label>
          <span className="text-gray-600">Invoice number prefix</span>
          <input
            className={`${inputClass} mt-1`}
            placeholder={settings.suggestedPrefix || 'NXT'}
            value={form.invoicePrefix}
            onChange={(e) => set({ invoicePrefix: e.target.value.toUpperCase() })}
          />
        </label>
        <label>
          <span className="text-gray-600">Starting invoice #</span>
          <input
            type="number"
            min={1}
            className={`${inputClass} mt-1`}
            value={form.startingInvoiceNo || 1}
            onChange={(e) => set({ startingInvoiceNo: Math.max(1, Math.floor(Number(e.target.value) || 1)) })}
          />
          <span className="text-gray-500 text-xs">
            The first invoice of the day uses this number. Later ones count up from there.
          </span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.remindersEnabled}
            onChange={(e) => set({ remindersEnabled: e.target.checked })}
          />
          <span>Send unpaid reminders on the global schedule</span>
        </label>
        <label>
          <span className="text-gray-600">Days before the due date (0 is the due date)</span>
          <input className={`${inputClass} mt-1`} value={daysText} onChange={(e) => setDaysText(e.target.value)} />
        </label>
        <label>
          <span className="text-gray-600">After the due date, remind every N days (0 turns this off)</span>
          <input
            type="number"
            min={0}
            className={`${inputClass} mt-1`}
            value={form.overdueEveryDays}
            onChange={(e) => set({ overdueEveryDays: Number(e.target.value) || 0 })}
          />
        </label>
        <label>
          <span className="text-gray-600">Reminder recipients</span>
          <textarea
            className={`${inputClass} mt-1`}
            rows={2}
            placeholder="accounting@company.com"
            value={form.reminderEmails}
            onChange={(e) => set({ reminderEmails: e.target.value })}
          />
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={form.notifyBillTo} onChange={(e) => set({ notifyBillTo: e.target.checked })} />
          <span>Also email the bill-to address on the invoice</span>
        </label>
        <label>
          <span className="text-gray-600">Paid-notice recipients</span>
          <textarea
            className={`${inputClass} mt-1`}
            rows={2}
            value={form.paidEmails}
            onChange={(e) => set({ paidEmails: e.target.value })}
          />
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.notifyBillToOnPaid}
            onChange={(e) => set({ notifyBillToOnPaid: e.target.checked })}
          />
          <span>Also email the bill-to address when the invoice is paid</span>
        </label>
        <label>
          <span className="text-gray-600">Default payment instructions</span>
          <textarea
            className={`${inputClass} mt-1`}
            rows={3}
            value={form.paymentNote}
            onChange={(e) => set({ paymentNote: e.target.value })}
          />
        </label>
      </div>
    </Modal>
  )
}
